/**
 * Form to Backend Value Transformation
 *
 * This module handles the transformation of form values back to backend
 * API format. The main transformations are:
 *
 * 1. Converting arrays with 'key' property back to object maps
 * 2. Removing frontend-only fields (_internalId, _isNewlyAppended)
 * 3. Converting frontend status keys back to backend status keys
 * 4. Filtering out incomplete/empty entries
 */
import { keyedArrayToObject } from "src/cases/templates/formUtils/objectArrayConverters";
import {
  createStatusKeyMappings,
  STATUS_DELIMITER,
} from "src/cases/templates/formUtils/statusKeyMapping";
import {
  CaseTemplateActionFormValue,
  CaseTemplateFormFieldsInner,
  CaseTemplateFormValues,
  CaseTemplateTaskFormValue,
  CaseTemplateWorkflowRuleFormValue,
  EnumPropertyValueForm,
} from "src/cases/templates/formUtils/types";
import { isStatusProperty } from "src/cases/utils";
import { JsonApiResourceObjectCaseTemplateVersionAttributesBaseModel } from "src/clients/case-management";
import { deepOmit } from "src/utils/objectUtils";

// =============================================================================
// FORM FIELD CLEANERS
// These functions remove unnecessary data before sending to backend.
// =============================================================================

/**
 * Removes the 'options' array from fields that don't need it.
 * Only select/multi_select fields should have options.
 *
 * @param field - Form field definition
 * @returns Field without unnecessary options
 */
const removeUnusedFieldOptions = (field: CaseTemplateFormFieldsInner) => {
  // Remove options array for field types that don't need it
  const selectFields = ["select", "multi_select"];
  if (selectFields.includes(field.type)) return field;

  const { options: _options, ...fieldWithoutOptions } =
    field as unknown as Record<string, unknown>;

  return fieldWithoutOptions;
};

/**
 * Type guard for objects with form.fields structure.
 */
const hasFormWithFields = (
  input: unknown,
): input is { form: { fields: unknown[] } } => {
  return (
    typeof input === "object" &&
    input !== null &&
    "form" in input &&
    typeof input.form === "object" &&
    input.form !== null &&
    "fields" in input.form &&
    Array.isArray(input.form.fields)
  );
};

/**
 * Cleans form fields within actions or tasks before sending to backend.
 * Removes unused options from field definitions.
 *
 * @param input - Action or task with optional form definition
 * @returns Cleaned action or task
 */
const cleanActionOrTaskFormFields = (
  input: CaseTemplateActionFormValue | CaseTemplateTaskFormValue,
) => {
  if (hasFormWithFields(input)) {
    return {
      ...input,
      form: {
        ...(input.form as unknown as Record<string, unknown>),
        fields: input.form.fields.map((field) =>
          removeUnusedFieldOptions(field as CaseTemplateFormFieldsInner),
        ),
      },
    };
  }
  return input;
};

// =============================================================================
// WORKFLOW RULE TRANSFORMATION
// Complex transformation that maps form rules back to backend format.
// =============================================================================

/**
 * Transforms workflow rules from form format back to backend format.
 *
 * Key transformations:
 * 1. Maps rule keys from frontend format ("uuid#uuid") to backend format ("open-new")
 * 2. Removes _internalId from all nested objects
 * 3. Converts status automation values from frontend to backend format
 * 4. Re-creates sub_status automation fields when status has a substatus
 * 5. Filters out rules with no actions or tasks
 * 6. Filters out actions/tasks with empty keys
 * 7. Filters out automations referencing deleted statuses
 *
 * @param workflowRules - Form workflow rules with frontend status keys
 * @param frontendToBackendStatusKeys - Map from frontend to backend status keys
 * @returns Backend workflow rules object
 */
const transformWorkflowRulesToBackendFormat = <
  T extends CaseTemplateWorkflowRuleFormValue,
>(
  workflowRules: T,
  frontendToBackendStatusKeys: Map<string, string>,
): { [key: string]: CaseTemplateWorkflowRuleFormValue[string] } => {
  const entries = Object.entries(workflowRules || {})
    .map(([frontendKey, rule]) => {
      const backendStatusKey = frontendToBackendStatusKeys.get(frontendKey);

      // Filter out rules where the status was deleted
      if (!backendStatusKey) {
        return undefined;
      }

      return [
        backendStatusKey,
        {
          ...rule,
          // Clean workflow tasks - filter empty keys, strip internal IDs
          workflow_tasks_keys: rule.workflow_tasks_keys
            ?.filter((task) => task.task_key)
            ?.map((task) => ({
              task_key: task.task_key,
              order: task.order,
            })),
          // Clean actions - filter empty keys, transform automations
          actions: rule.actions
            ?.filter((action) => action.action_key)
            ?.map((action) => {
              return {
                action_key: action.action_key,
                // Transform automation values back to backend format
                automation: action.automation?.flatMap((automation) => {
                  if (automation.field === "status") {
                    const backendStatusValue = frontendToBackendStatusKeys.get(
                      String(automation.value),
                    );

                    // Filter out automations referencing deleted statuses
                    if (!backendStatusValue) {
                      return [];
                    }

                    // Split backend key into status and optional sub_status
                    const [status, subStatus] =
                      backendStatusValue.split(STATUS_DELIMITER);

                    if (!subStatus) {
                      return [
                        {
                          ...automation,
                          value: status,
                        },
                      ];
                    }

                    // Re-create separate status and sub_status automation entries
                    return [
                      {
                        ...automation,
                        value: status,
                      },
                      {
                        field: "sub_status",
                        value: subStatus,
                      },
                    ];
                  }

                  return [automation];
                }),
                // Clean required tasks - strip internal IDs
                required_tasks_keys: action.required_tasks_keys?.map(
                  (reqTask) => ({
                    task_key: reqTask.task_key,
                  }),
                ),
              };
            }),
        },
      ] as const;
    })
    // Filter out deleted status rules and empty rules
    .filter((entry): entry is NonNullable<typeof entry> => {
      if (!entry) {
        return false;
      }

      const [, rule] = entry;

      // Keep rule if it has any actions or tasks
      return (
        !!(rule as CaseTemplateWorkflowRuleFormValue[string]).actions?.length ||
        !!(rule as CaseTemplateWorkflowRuleFormValue[string])
          .workflow_tasks_keys?.length
      );
    });

  return Object.fromEntries(
    entries as [string, CaseTemplateWorkflowRuleFormValue[string]][],
  );
};

// =============================================================================
// MAIN ENTRY POINT
// =============================================================================

/**
 * Transforms form values back to backend format.
 *
 * This is the main entry point for Form -> Backend transformation.
 * Called when saving the form or comparing for dirty checking.
 *
 * @param formValues - Form values from React Hook Form
 * @returns Backend API format ready for submission
 *
 * @example
 * const handleSubmit = (data: CaseTemplateFormValues) => {
 *   const backendData = transformFormValuesToTemplate(data);
 *   await api.updateTemplate(backendData);
 * };
 */
export const transformFormValuesToTemplate = (
  formValues: CaseTemplateFormValues,
): JsonApiResourceObjectCaseTemplateVersionAttributesBaseModel => {
  // Build status key mappings for workflow rule transformation
  const statusProperty =
    formValues.attributes.case_properties?.find(isStatusProperty);

  // Create mappings from frontend keys to backend keys
  const statusKeyMappings = statusProperty
    ? createStatusKeyMappings((statusProperty as EnumPropertyValueForm).values)
    : { backendToFrontend: new Map(), frontendToBackend: new Map() };

  // Drop additional_inputs from form attributes to avoid sending it to the backend
  // We do not support additional_inputs in the backend anymore
  // But they can come in legacy templates, so we need to drop them.
  const { additional_inputs: _, ...formAttributes } = formValues.attributes;

  return {
    ...formValues,
    attributes: {
      ...formAttributes,
      // Stamp order from array position, remove _internalId from case properties
      case_properties: keyedArrayToObject(
        deepOmit(formValues.attributes.case_properties, "_internalId")?.map(
          (property, index) => ({
            ...property,
            order: index,
          }),
        ),
      ),
      file_categories: keyedArrayToObject(
        formValues.attributes.file_categories,
      ),
      // Transform actions: clean form fields, add order, remove internal IDs
      case_actions: keyedArrayToObject(
        formValues.attributes.case_actions?.map((action, index) => ({
          ...cleanActionOrTaskFormFields(deepOmit(action, "_internalId")),
          order: index,
        })),
        {
          excludeKey: false,
        },
      ),
      // Transform workflow rules back to backend format
      workflow_rules: transformWorkflowRulesToBackendFormat(
        formValues.attributes.workflow_rules,
        statusKeyMappings.frontendToBackend,
      ),
      // Transform tasks: clean form fields, add order, remove internal IDs
      case_tasks: keyedArrayToObject(
        formValues.attributes.case_tasks?.map((task, index) => ({
          ...cleanActionOrTaskFormFields(deepOmit(task, "_internalId")),
          order: index,
        })),
        {
          excludeKey: false,
        },
      ),
      // Transform case blocks: convert array back to object, strip _internalId
      case_blocks_configs: keyedArrayToObject(
        deepOmit(formValues.attributes.case_blocks_configs, "_internalId"),
      ),
      // Null-out copilot_settings when copilot is disabled
      copilot_settings: formAttributes.copilot_settings?.copilot_enabled
        ? formAttributes.copilot_settings
        : null,
    },
  } as unknown as JsonApiResourceObjectCaseTemplateVersionAttributesBaseModel;
};
